import {
  Shipment,
  ShipmentStopType,
  Stop,
} from "@api/graphql/generated/generated-types";

export interface JobDuration {
  /** Human-readable "Xh Ym" label, e.g. "2h 15m". */
  label: string;
  /** Duration in decimal hours (for payroll and pay calculation). */
  decimalHours: number;
}

/**
 * Coerce a Timestamp scalar (epoch seconds) to a number of milliseconds.
 * The codebase types this field inconsistently (string in some places,
 * number in others), so Number() handles both.
 */
function timestampToMillis(value: unknown): number | undefined {
  if (value === null || value === undefined) return undefined;
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return undefined;
  return seconds * 1000;
}

/**
 * Earliest pickup arrival across all PICK_UP stops' tasks, in millis.
 * A stop may have multiple tasks; we consider every task's arrivedAt.
 */
function earliestPickupArrivalMillis(stops: Stop[]): number | undefined {
  const arrivals = stops
    .filter((stop) => stop.type === ShipmentStopType.PickUp)
    .flatMap((stop) => stop.tasks ?? [])
    .map((task) => timestampToMillis(task.arrivedAt))
    .filter((millis): millis is number => millis !== undefined);
  return arrivals.length ? Math.min(...arrivals) : undefined;
}

/**
 * Latest dropoff completion across all DROP_OFF stops, in millis.
 */
function latestDropoffCompletionMillis(stops: Stop[]): number | undefined {
  const completions = stops
    .filter((stop) => stop.type === ShipmentStopType.DropOff)
    .map((stop) => timestampToMillis(stop.completedAt))
    .filter((millis): millis is number => millis !== undefined);
  return completions.length ? Math.max(...completions) : undefined;
}

/**
 * Elapsed job duration from pickup arrival to dropoff completion.
 *
 * start = earliest arrivedAt across PICK_UP stops' tasks
 * end   = latest completedAt across DROP_OFF stops
 *
 * Returns null when either side is missing (incomplete job) or when
 * end <= start (clock-skew guard) — never a negative or zero duration.
 */
export function getJobDuration(
  shipment: Shipment | undefined
): JobDuration | null {
  const stops = shipment?.stops;
  if (!stops || stops.length === 0) return null;

  const start = earliestPickupArrivalMillis(stops);
  const end = latestDropoffCompletionMillis(stops);
  if (start === undefined || end === undefined) return null;
  if (end <= start) return null;

  const totalMinutes = Math.round((end - start) / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    label: `${hours}h ${minutes}m`,
    // Derived from the same rounded minutes shown in the label so displayed
    // duration and calculated pay always agree.
    decimalHours: totalMinutes / 60,
  };
}

/**
 * Estimated driver pay = decimalHours × regular hourly rate, in dollars.
 *
 * `driver.hourlyRateRegular` is a dollar-denominated Money value (e.g. "25.00").
 * Returns null when the driver has no positive regular rate — the "is hourly"
 * guard — so flat-rate drivers show no pay row. Overtime is out of scope for v1.
 */
export function getEstimatedPay(
  shipment: Shipment | undefined,
  decimalHours: number
): number | null {
  const rate = shipment?.driver?.hourlyRateRegular;
  if (rate === null || rate === undefined) return null;

  const hourlyRate = Number(rate);
  if (!Number.isFinite(hourlyRate) || hourlyRate <= 0) return null;

  return decimalHours * hourlyRate;
}
